import type { CityConfig } from "@/pages/city-template/types";

export const mcminnvilleConfig: CityConfig = {
  city: "McMinnville",
  state: "Oregon",
  stateAbbr: "OR",
  slug: "mcminnville",
  route: "/cities/mcminnville",
  title: "Emergency Passport McMinnville OR | Expedited Passport Yamhill County Oregon",
  metaDescription: "Emergency passport and expedited passport services in McMinnville, OR — as fast as 24 hours. Fast Passport Center serves McMinnville, Yamhill County, Newberg, Dundee, Sheridan, and the Willamette Valley wine country. Registered State Dept. courier. A+ BBB rated. No office visit required.",
  h1: "Emergency & Expedited Passport Services in McMinnville, Oregon",
  heroSubtitle: "Wine industry trade trip to France or Italy booked? Linfield University study abroad deadline approaching? Family vacation to Mexico and your passport expired? Fast Passport Center helps McMinnville and Yamhill County residents get expedited passports as fast as 24 hours — entirely remotely. The Seattle Passport Agency is 280 miles away, but we serve you without the drive.",
  heroImageUrl: "https://readdy.ai/api/search-image?query=McMinnville%20Oregon%20historic%20downtown%20Third%20Street%20with%20tree%20lined%20sidewalks%20and%20classic%20brick%20storefronts%20nestled%20in%20Yamhill%20County%20wine%20country%2C%20the%20iconic%20Hotel%20Oregon%20building%20at%20dusk%20with%20warm%20golden%20light%2C%20surrounding%20vineyards%20and%20rolling%20hills%20of%20Pinot%20Noir%20grapevines%2C%20Pacific%20Northwest%20small%20town%20charm%20with%20dramatic%20evening%20sky%2C%20cinematic%20atmospheric%20quality%2C%20no%20text%20no%20people&width=1800&height=700&seq=mcminnville-or-hero-01&orientation=landscape",
  heroImageAlt: "McMinnville Oregon historic downtown Third Street in Yamhill County wine country",
  geoPosition: "45.2101;-123.1985",
  geoPlacename: "McMinnville, Oregon",
  lastModified: "2026-06-14",
  regionalAgency: {
    name: "Seattle Passport Agency",
    address: "915 2nd Ave Suite 500, Seattle WA 98174",
    distance: "About 280 miles north — approximately 5 hours by car via OR-99W and I-5",
  },
  nearbyCitiesZips: [
    { name: "McMinnville", zips: "97128" },
    { name: "Newberg", zips: "97132" },
    { name: "Dundee", zips: "97115" },
    { name: "Lafayette", zips: "97127" },
    { name: "Carlton", zips: "97111" },
    { name: "Dayton", zips: "97114" },
    { name: "Sheridan", zips: "97378" },
    { name: "Yamhill", zips: "97148" },
    { name: "Amity", zips: "97101" },
    { name: "Gaston", zips: "97119" },
    { name: "Willamina", zips: "97396" },
    { name: "Sherwood", zips: "97140" },
  ],
  benefits: [
    { icon: "ri-computer-line", title: "Start 100% Online from McMinnville", desc: "Complete your emergency or expedited passport order from anywhere in Yamhill County — no road trip to Seattle or Portland required." },
    { icon: "ri-truck-line", title: "Overnight Shipping from McMinnville or Salem", desc: "Ship your documents from The UPS Store in McMinnville or Salem FedEx Office — both support overnight delivery for expedited submissions." },
    { icon: "ri-shield-check-line", title: "Expert Document Review", desc: "Every application reviewed before submission — critical for Yamhill County residents with wine industry and academic travel needs." },
    { icon: "ri-calendar-check-line", title: "Reserved Agency Submission Access", desc: "We bypass the public appointment backlog with reserved Seattle Passport Agency slots — far faster than calling for an emergency appointment." },
  ],
  testimonials: [
    {
      name: "David M.",
      location: "McMinnville, OR",
      rating: 5,
      text: "I had a wine industry trade trip to Burgundy, France booked and realized my passport was expired three weeks before departure. Fast Passport Center renewed it in 5 business days. They guided me through the overnight shipping from McMinnville. Absolutely saved the trip.",
      service: "Expedited Passport Renewal",
    },
    {
      name: "Jennifer T.",
      location: "Newberg, OR",
      rating: 5,
      text: "Our daughter studies at Linfield and needed a passport for a study abroad program in Spain. We had 18 days. Fast Passport Center got her first-time passport processed in 7 business days. Amazing service for Yamhill County families.",
      service: "First-Time Passport Expedited",
    },
    {
      name: "Robert K.",
      location: "Dundee, OR",
      rating: 5,
      text: "We booked a family trip to Italy for wine country touring and my wife realized her passport was expiring before our return date. Fast Passport Center handled the renewal in 6 business days from Dundee. Seamless experience — didn't even need to drive to Portland.",
      service: "Expedited Passport Processing",
    },
  ],
  facilities: [
    {
      id: "mcminnville-post-main",
      name: "McMinnville Post Office",
      type: "USPS Passport Acceptance",
      address: "650 NE 2nd St",
      city: "McMinnville",
      state: "OR",
      zip: "97128",
      phone: "800-275-8777",
      hours: "Mon–Fri 9am–5pm",
      distance: "Downtown McMinnville — primary passport acceptance for McMinnville and Yamhill County",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "Schedule Appointment",
      notes: "Primary passport acceptance facility for Yamhill County residents. Appointment required. Good availability compared to coastal and rural Oregon locations.",
      icon: "ri-mail-line",
    },
    {
      id: "yamhill-county-clerk",
      name: "Yamhill County Clerk's Office",
      type: "County Clerk Passport Acceptance",
      address: "535 NE 5th St, Room 33",
      city: "McMinnville",
      state: "OR",
      zip: "97128",
      phone: "(503) 434-7518",
      hours: "Mon–Fri 8:30am–5pm",
      distance: "Downtown McMinnville — Yamhill County Courthouse",
      website: "https://www.co.yamhill.or.us/clerk",
      websiteLabel: "Yamhill County Clerk",
      notes: "Yamhill County Clerk in the Courthouse accepts passport applications. Often the best appointment availability in the county — recommended first choice over the post office.",
      icon: "ri-government-line",
    },
    {
      id: "newberg-post",
      name: "Newberg Post Office",
      type: "USPS Passport Acceptance",
      address: "115 N College St",
      city: "Newberg",
      state: "OR",
      zip: "97132",
      phone: "800-275-8777",
      hours: "Mon–Fri 9am–5pm",
      distance: "Newberg — about 14 miles northeast of McMinnville via OR-99W",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "Schedule Appointment",
      notes: "Alternative acceptance facility for northern Yamhill County residents in Newberg and Dundee. Good availability.",
      icon: "ri-mail-line",
    },
    {
      id: "salem-post",
      name: "Salem Main Post Office",
      type: "USPS Passport Acceptance",
      address: "1050 25th St SE",
      city: "Salem",
      state: "OR",
      zip: "97301",
      phone: "800-275-8777",
      hours: "Mon–Fri 9am–5pm",
      distance: "Salem — about 25 miles southeast via OR-99W and I-5",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "Schedule Appointment",
      notes: "Larger facility in Salem with more appointment slots than McMinnville locations. Recommended for urgent situations when McMinnville is booked.",
      icon: "ri-mail-line",
    },
    {
      id: "sherwood-post",
      name: "Sherwood Post Office",
      type: "USPS Passport Acceptance",
      address: "16055 SW Tualatin-Sherwood Rd",
      city: "Sherwood",
      state: "OR",
      zip: "97140",
      phone: "800-275-8777",
      hours: "Mon–Fri 9am–5pm",
      distance: "Sherwood — about 15 miles east via OR-99W",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "Schedule Appointment",
      notes: "Alternative for eastern Yamhill County and McMinnville residents willing to drive toward the Portland metro area.",
      icon: "ri-mail-line",
    },
    {
      id: "tillamook-post-alt",
      name: "Tillamook Post Office",
      type: "USPS Passport Acceptance",
      address: "2200 1st St",
      city: "Tillamook",
      state: "OR",
      zip: "97141",
      phone: "800-275-8777",
      hours: "Mon–Fri 9am–5pm",
      distance: "Tillamook — about 35 miles west via OR-6 through the coast range",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "Schedule Appointment",
      notes: "Alternative for western Yamhill County residents willing to drive through the coast range. Limited appointment availability in coastal summer months.",
      icon: "ri-mail-line",
    },
  ],
  faqs: [
    {
      q: "Can I get an emergency passport from McMinnville, Oregon?",
      a: "Yes. Fast Passport Center handles emergency passport processing for McMinnville residents as fast as 24 hours. The Seattle Passport Agency is about 5 hours from McMinnville — we serve you entirely remotely via overnight shipping from The UPS Store in McMinnville or Salem FedEx Office. Start online, we handle the rest.",
    },
    {
      q: "How fast can I get an expedited passport from McMinnville, OR?",
      a: "Expedited passport processing from McMinnville takes 2–9 business days through Fast Passport Center. Most adult renewals don't require an in-person acceptance visit — ship overnight from The UPS Store in McMinnville or a Salem FedEx Office.",
    },
    {
      q: "Where is the passport acceptance office in McMinnville?",
      a: "McMinnville's primary passport acceptance locations are the McMinnville Post Office (650 NE 2nd St) and the Yamhill County Clerk's Office (535 NE 5th St), both in downtown McMinnville. For the best appointment availability, start with the County Clerk.",
    },
    {
      q: "Does McMinnville's wine industry generate international passport needs?",
      a: "Yes — McMinnville is the heart of Oregon Pinot Noir country and Yamhill County wine professionals frequently travel to Burgundy, Bordeaux, Tuscany, Rioja, and the Rheingau for viticulture conferences, wine trade expos (ProWein, Vinexpo), and harvest exchange programs. These trips often have 4–8 week lead times — perfect for expedited passport processing.",
    },
    {
      q: "How far is McMinnville from the Seattle Passport Agency?",
      a: "McMinnville is approximately 280 miles from the Seattle Passport Agency — about 5 hours by car via OR-99W and I-5. For life-or-death emergencies requiring same-day processing, a Seattle trip may be necessary. For most urgent travel needs, Fast Passport Center serves McMinnville entirely remotely.",
    },
    {
      q: "Does Linfield University generate study abroad passport needs?",
      a: "Yes — Linfield University has active study abroad programs sending students to Spain, France, Italy, Japan, and Costa Rica. Students often discover passport requirements 3–6 weeks before program departure. Fast Passport Center handles first-time and renewal passports for Linfield students in 2–9 business days.",
    },
    {
      q: "What airport do McMinnville residents use for international travel?",
      a: "Portland International Airport (PDX) is the primary international hub for McMinnville travelers — about 45 miles northeast via OR-99W and I-5 (about 1 hour). PDX serves direct international routes to Amsterdam, London, Tokyo, Vancouver BC, and Mexico City.",
    },
    {
      q: "Can I renew my passport from McMinnville without visiting an office?",
      a: "Yes. Most adult McMinnville residents renewing their passport can do so entirely by mail — no in-person acceptance visit required for renewals. Ship overnight from The UPS Store McMinnville or Salem FedEx, and we handle the Seattle agency submission.",
    },
    {
      q: "Can I get a child passport from McMinnville?",
      a: "Yes. Child passports require an in-person acceptance visit at the McMinnville Post Office or Yamhill County Clerk's Office — both parents must typically be present or provide notarized consent. Fast Passport Center provides the child passport checklist and expedites after the acceptance step.",
    },
    {
      q: "How much does expedited passport processing cost from McMinnville?",
      a: "Fast Passport Center pricing includes the U.S. government passport fees plus our courier service fee — which is typically 30–100% lower than FedEx Office or retail passport services. We provide an exact price quote at checkout before any commitment.",
    },
  ],
  photoLocations: [
    { name: "Walgreens — McMinnville", address: "1550 N Baker St, McMinnville, OR 97128", hours: "Daily 8 AM – 10 PM" },
    { name: "The UPS Store — McMinnville", address: "2349 NE Highway 99W, McMinnville, OR 97128", hours: "Mon–Fri 8:30 AM – 6 PM, Sat 10 AM – 3 PM" },
    { name: "Rite Aid — McMinnville", address: "615 NE Baker St, McMinnville, OR 97128", hours: "Daily 8 AM – 10 PM" },
    { name: "Newberg Post Office", address: "115 N College St, Newberg, OR 97132", hours: "Mon–Fri 9 AM – 5 PM" },
    { name: "FedEx Office — Salem", address: "1101 Hawthorne Ave SE, Salem, OR 97301", hours: "Mon–Fri 7 AM – 10 PM, Sat–Sun 9 AM – 6 PM" },
    { name: "Walgreens — Newberg", address: "2900 Portland Rd, Newberg, OR 97132", hours: "Daily 8 AM – 10 PM" },
    { name: "Walgreens — Salem South", address: "5650 Commercial St SE, Salem, OR 97306", hours: "Daily 8 AM – 10 PM" },
    { name: "Costco Photo Center — Hillsboro", address: "1255 NE 48th Ave, Hillsboro, OR 97124", hours: "Mon–Fri 10 AM – 8:30 PM, Sat–Sun 9:30 AM – 6 PM" },
  ],
  dmvLocations: [
    {
      name: "McMinnville Driver & Motor Vehicle Services",
      address: "435 NE Evans St, McMinnville, OR 97128",
      hours: "Mon–Fri 8:30 AM – 5:00 PM",
      note: "McMinnville DMV — driver licenses, REAL ID, and state ID cards for Yamhill County residents.",
      featured: true,
    },
    {
      name: "Salem Driver & Motor Vehicle Services",
      address: "3809 Center St NE, Salem, OR 97301",
      hours: "Mon–Fri 8:30 AM – 5:00 PM",
      note: "Salem DMV — alternative with full services for Yamhill County residents. About 25 miles south.",
      featured: false,
    },
    {
      name: "Hillsboro Driver & Motor Vehicle Services",
      address: "1300 SW Oak St, Hillsboro, OR 97123",
      hours: "Mon–Fri 8:30 AM – 5:00 PM",
      note: "Hillsboro DMV — alternative for northern Yamhill County residents. About 30 miles northeast.",
      featured: false,
    },
  ],
  airports: [
    {
      code: "PDX",
      name: "Portland International Airport",
      distance: "Primary international hub — about 45 miles northeast via OR-99W and I-5 (about 1 hour)",
      address: "7000 NE Airport Way, Portland, OR 97218",
      note: "Portland International Airport is the primary international departure hub for McMinnville and Yamhill County travelers. PDX offers direct flights to Amsterdam, London, Reykjavik, Tokyo, Vancouver BC, and Mexico City.",
      flightUrl: "https://www.flypdx.com/",
      flightLabel: "PDX Airport Website",
      featured: true,
    },
    {
      code: "SEA",
      name: "Seattle-Tacoma International Airport",
      distance: "About 280 miles north via I-5 (approximately 5 hours)",
      address: "17801 International Blvd, Seattle, WA 98158",
      note: "SEA-TAC is an alternative major international hub with more flight options, especially for Asia, Alaska, and European routes.",
      flightUrl: "https://www.portseattle.org/sea-tac",
      flightLabel: "SEA-TAC Airport Website",
      featured: false,
    },
    {
      code: "SLE",
      name: "Salem Municipal Airport",
      distance: "About 25 miles south — limited regional service only",
      address: "2990 25th St SE, Salem, OR 97302",
      note: "Regional airport with minimal commercial service. Portland International (PDX) is strongly preferred for all Yamhill County international travel.",
      flightUrl: "https://www.cityofsalem.net/airport",
      flightLabel: "SLE Airport Website",
      featured: false,
    },
  ],
  services: [
    {
      icon: "ri-time-line",
      title: "Emergency Passport — McMinnville & Yamhill County",
      description: "Wine industry trip to France imminent? Linfield study abroad deadline approaching? Need a passport fast from Yamhill County? Fast Passport Center handles emergency passport processing for McMinnville residents — as fast as 24 hours. Serve entirely remotely.",
      bullets: ["As fast as 24 hours processing", "Reserved Seattle agency submission", "Dedicated agent and live status tracking"],
      badge: "Same-Day Available",
      link: "https://fastpassportcenter.com/checkout/?passport=emergency-passport",
    },
    {
      icon: "ri-refresh-line",
      title: "Expedited Passport Renewal",
      description: "Passport expired and you have a wine trade trip to Europe coming up? McMinnville residents renew fast through Fast Passport Center — ship from The UPS Store McMinnville or Salem FedEx overnight, we handle the expedited submission.",
      bullets: ["Fastest renewal for Yamhill County residents", "Ship overnight from McMinnville or Salem", "Professional document review included"],
      badge: "Most Popular",
      link: "https://fastpassportcenter.com/checkout/?passport=passport-renewal",
    },
    {
      icon: "ri-search-eye-line",
      title: "Lost Passport Replacement",
      description: "Lost your passport in McMinnville or before a wine country trip to Europe? We guide Yamhill County travelers through DS-64 and expedited replacement — no Seattle trip required.",
      bullets: ["DS-64 theft/loss reporting guidance", "Expedited replacement timelines available", "Document review included"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=lost-stolen-passport",
    },
    {
      icon: "ri-parent-line",
      title: "Child Passport — McMinnville Families",
      description: "McMinnville and Yamhill County families heading to Canada, Mexico, or European vacations need child passports. We guide you through the McMinnville Post Office or County Clerk acceptance step.",
      bullets: ["Both parents typically required to consent", "McMinnville Post Office or County Clerk guidance", "Expedited after in-person acceptance step"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=child-passport",
    },
    {
      icon: "ri-file-add-line",
      title: "First-Time Passport Applications",
      description: "First passport from McMinnville? In-person acceptance at the McMinnville Post Office or Yamhill County Clerk is required for first-timers. We guide you through every step and expedite from there.",
      bullets: ["Step-by-step acceptance guidance for McMinnville facilities", "Photo location guide for McMinnville and Yamhill County", "Expedited processing after clerk visit"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-truck-line",
      title: "Registered State Dept. Courier Service",
      description: "Fast Passport Center is a registered U.S. Department of State courier — not a reseller. We submit through reserved agency channels at 30–100% lower prices than retail services.",
      bullets: ["State Dept. registered courier", "Best price guarantee", "Not affected by government shutdowns"],
      badge: "Registered Courier",
      link: "https://fastpassportcenter.com/checkout/",
    },
    {
      icon: "ri-edit-line",
      title: "Name Change & Specialty Passport Services",
      description: "Name change passports, damaged passport replacement, second passports for wine industry professionals, frequent international travelers, and Yamhill County residents with complex passport needs.",
      bullets: ["Name change after marriage", "Damaged passport replacement", "Second passport for wine industry travelers"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=name-change",
    },
  ],
  servicesSchema: [
    { name: "Emergency Passport", description: "Same-day emergency passport processing for McMinnville, Oregon residents with urgent international travel needs." },
    { name: "Expedited Passport", description: "Expedited passport processing for McMinnville and Yamhill County residents in 2–9 business days." },
    { name: "Passport Renewal", description: "Fast passport renewal for McMinnville, Oregon — ship overnight from The UPS Store McMinnville or Salem FedEx." },
    { name: "Lost Passport Replacement", description: "Emergency passport replacement for McMinnville travelers with lost or stolen passports." },
    { name: "Child Passport", description: "Expedited child passport services for McMinnville and Yamhill County families." },
    { name: "Passport Courier Service", description: "Registered U.S. Department of State courier serving McMinnville, Oregon and Yamhill County wine country." },
  ],
  mistakes: [
    { icon: "ri-time-line", title: "Booking European Wine Tours Without Checking Passport Validity", description: "Yamhill County wine enthusiasts love touring Burgundy, Bordeaux, and Tuscany. Many book wine tours and flights months ahead but forget to check passport expiration until 2–3 weeks before departure — the classic wine country passport panic." },
    { icon: "ri-camera-off-line", title: "Submitting Poor-Quality Passport Photos", description: "Photos from drugstores or self-taken phone photos frequently get rejected for lighting or expression issues. Fast Passport Center reviews photo specifications before submission." },
    { icon: "ri-file-damage-line", title: "Incomplete Forms or Missing Signatures", description: "One missing signature returns your application — costing weeks when you're already on tight timelines for wine trade shows or study abroad programs. Our pre-submission review catches all errors." },
    { icon: "ri-pen-nib-line", title: "Wrong Birth Certificate Format", description: "Short-form birth certificates are rejected. We specify exactly what document format is needed based on your state of birth." },
    { icon: "ri-graduation-cap-line", title: "Linfield Study Abroad — Last-Minute Passport Discovery", description: "Linfield University students frequently discover passport requirements 3–6 weeks before study abroad program departure. Start passport applications the same day you apply to the study abroad program." },
    { icon: "ri-store-2-line", title: "Wine Trade Show Travel — Short Lead Times", description: "Vinexpo, ProWein, and other wine trade events have fixed dates with 4–8 weeks notice for attendees. Yamhill County wine professionals should maintain a valid passport at all times — trade show invitations don't wait for passport processing." },
  ],
  steps: [
    { number: "01", icon: "ri-computer-line", title: "Start Your Order Online", description: "Choose your processing speed based on your McMinnville travel date. Complete your secure order from home in minutes." },
    { number: "02", icon: "ri-file-list-3-line", title: "Receive Your Personalized Checklist", description: "We send a personalized document checklist — exactly what to gather, with McMinnville and Yamhill County acceptance location guidance." },
    { number: "03", icon: "ri-send-plane-line", title: "Ship Documents Overnight", description: "Drop off at The UPS Store in McMinnville or Salem FedEx Office for overnight delivery. Both are reliable options for Yamhill County residents." },
    { number: "04", icon: "ri-building-2-line", title: "We Submit Through Reserved Agency Access", description: "We submit through reserved Seattle Passport Agency slots — bypassing the public appointment backlog that stretches for weeks." },
    { number: "05", icon: "ri-passport-line", title: "Passport Shipped to Your Door", description: "Your completed passport ships directly to your McMinnville or Yamhill County address. Live status tracking included." },
  ],
  processingOptions: [
    {
      speed: "24",
      unit: "Hours*",
      label: "Regional Agency Processing",
      tag: "Emergency Only",
      tagColor: "bg-gray-100 text-gray-500",
      features: [
        "Must drive to Seattle — 5 hours from McMinnville",
        "Life-or-death emergencies only",
        "Very limited appointment availability",
        "Long wait times even with appointment",
      ],
      highlight: false,
      emergency: true,
    },
    {
      speed: "2–9",
      unit: "Business Days",
      label: "Fast Passport Center",
      tag: "Most Popular",
      tagColor: "bg-[#F6C344] text-[#1A365D]",
      features: [
        "Registered U.S. Department of State Courier",
        "Reserved agency submission",
        "Ship overnight from McMinnville or Salem",
        "Document review included",
        "Dedicated agent on your case",
        "Daily status updates",
        "Best price guarantee",
        "Not affected by government shutdowns",
      ],
      highlight: true,
    },
    {
      speed: "3–5",
      unit: "Weeks",
      label: "Fast Passport Center",
      tag: "Standard Expedited",
      tagColor: "bg-[#1A365D]/10 text-[#1A365D]",
      features: [
        "Registered U.S. Department of State Courier",
        "Reserved agency submission",
        "Faster than USPS self-service",
        "Document review included",
        "Dedicated agent",
        "Not affected by government shutdowns",
      ],
      highlight: false,
      smartSaver: true,
    },
    {
      speed: "1–2",
      unit: "Months",
      label: "Self-Service (Post Office)",
      tag: "Slowest",
      tagColor: "bg-gray-100 text-gray-400",
      features: [
        "Standard mail — no courier access",
        "6–13 weeks; can be longer in summer",
        "In-person clerk appointment required",
        "No document review — errors cause rejection",
        "No dedicated agent",
        "Not suitable if travel is under 10 weeks away",
      ],
      highlight: false,
      muted: true,
    },
  ],
  comparisonRows: [
    { feature: "Appointment Required", agency: "Yes — weeks-long wait, Seattle only", fpc: "No appointment needed to start" },
    { feature: "In-Person Visit", agency: "Drive to Seattle — 5 hours from McMinnville", fpc: "Ship overnight from McMinnville or Salem" },
    { feature: "Processing Speed", agency: "Strict eligibility requirements", fpc: "As fast as 24 hours — multiple tiers" },
    { feature: "Document Review", agency: "None — errors cause rejection", fpc: "Professional review included" },
    { feature: "Yamhill County Accessibility", agency: "Not designed for Willamette Valley residents", fpc: "Fully remote service via overnight shipping" },
    { feature: "Government Shutdowns", agency: "May be impacted", fpc: "Not affected" },
    { feature: "Best Price Guarantee", agency: "Govt. fees + 10-hour Seattle round trip cost", fpc: "Best price guarantee — no travel required" },
  ],
  stickyNavLinks: [
    { label: "Processing Options", href: "#processing-options" },
    { label: "Services", href: "#services" },
    { label: "Service Area", href: "#service-area" },
    { label: "Acceptance Locations", href: "#acceptance-facilities" },
    { label: "vs. Regional Agency", href: "#comparison" },
    { label: "FAQ", href: "#faq" },
    { label: "Local Resources", href: "#local-resources" },
  ],
  internalLinks: [
    { name: "Expedited passport services in Newberg", route: "/cities/newberg" },
    { name: "Emergency passport help in Salem", route: "/cities/salem" },
    { name: "Fast passport renewal in Portland", route: "/cities/portland" },
    { name: "Rush passport services for Oregon residents", route: "/states/oregon" },
    { name: "Expedited passports statewide in Oregon", route: "/states/oregon" },
  ],
  fpcOfficeNote: "After placing your order online, we send your personalized checklist and overnight shipping instructions. Ship from The UPS Store in McMinnville or Salem FedEx Office — both available weekday evenings. No visit to our processing facility required for most services.",
  fpcOfficeDistance: "Serving McMinnville and the entire Yamhill County wine country remotely",
  serviceAreaIntro: "Whether you are in McMinnville, Newberg, Dundee, Carlton, Lafayette, Dayton, Sheridan, Amity, or anywhere in Yamhill County — Fast Passport Center makes emergency and expedited passport processing simple without a drive to Seattle.",
  nearbyCitiesIntro: "We serve all Yamhill County zip codes and surrounding communities including Newberg, Dundee, Carlton, Lafayette, Dayton, Sheridan, Amity, Yamhill, and Gaston.",
  howItWorksIntro: "Getting an emergency or expedited passport from McMinnville is simpler than a 5-hour drive to Seattle. Most Yamhill County clients never visit an office.",
  mistakesIntro: "These are the most common mistakes that cause delays for McMinnville and Yamhill County passport applicants. We review your documents before submission to prevent all of them.",
  testimonialsIntro: "Real reviews from McMinnville, Newberg, and Yamhill County wine country travelers who needed their passports fast.",
  faqIntro: "The most common questions McMinnville and Yamhill County residents ask about emergency and expedited passport services.",
  statePageRoute: "/states/oregon",
  statePageName: "Oregon",
  travelTiming: {
    title: "Travel & Passport Timing for McMinnville and Yamhill County Residents",
    intro: "McMinnville is the heart of Oregon wine country — surrounded by world-class Pinot Noir vineyards, home to Linfield University, and steeped in agricultural heritage. Its unique identity as a wine destination, college town, and agricultural hub creates very specific international travel patterns for Yamhill County residents.",
    cards: [
      {
        icon: "ri-store-2-line",
        title: "Wine Industry International Travel — the Yamhill County Factor",
        subtitle: "Oregon Pinot Noir drives unique passport needs to France, Italy, and beyond",
        description: "Yamhill County's wine industry generates significant international travel — to Burgundy and Bordeaux (French wine technology and viticulture exchange), Tuscany and Piedmont (Italian wine tradition), Rheingau (German wine science), and Rioja (Spanish wine culture). McMinnville wine professionals attend Vinexpo, ProWein, and other international wine trade expos. These trips are typically scheduled with 4–8 weeks notice — exactly the window for expedited passport processing.",
        tip: "Wine industry professionals in McMinnville should maintain a passport with at least 12 months of validity at all times. European wine expos often have short lead times — 2–9 business day expedited processing handles these timelines perfectly.",
      },
      {
        icon: "ri-graduation-cap-line",
        title: "Linfield University — Study Abroad Passport Rush",
        subtitle: "Linfield students create regular demand for first-time and urgent passports",
        description: "Linfield University runs active study abroad programs sending students to Spain, France, Italy, Japan, and Costa Rica each academic year. Students accepted to these programs typically have 3–6 weeks between acceptance and departure — and many need first-time passports. Fast Passport Center handles Linfield student passport processing regularly, especially during fall (August–October) and spring (January–March) program cycles.",
        tip: "Linfield students accepted to study abroad programs should begin passport applications the same day they receive their acceptance. First-time passports require an in-person acceptance visit at the McMinnville Post Office or Yamhill County Clerk — schedule immediately.",
      },
      {
        icon: "ri-leaf-line",
        title: "Italy & France Wine Country Tourism",
        subtitle: "Yamhill County wine lovers making pilgrimages to Old World wine regions",
        description: "McMinnville residents who live and breathe wine culture frequently plan trips to the classic wine regions of Europe — touring Burgundy and Bordeaux, renting villas in Tuscany, and exploring the Spanish Rioja. These trips are typically planned 3–6 months in advance, but passport expiration checks often happen at the last minute during booking.",
        tip: "Check passport expiration before booking any European wine country trip. France, Italy, and Spain all require passports valid for at least 3 months beyond planned departure from the Schengen Area.",
      },
      {
        icon: "ri-sun-line",
        title: "Costa Rica & South America Eco-Adventures",
        subtitle: "Yamhill County's connection to sustainable agriculture travel",
        description: "Yamhill County's organic farming and sustainable agriculture community frequently travels to Costa Rica, Ecuador, Peru, and Chile — for coffee and sustainable ag exchanges, eco-tourism, and winter escapes. These trips are typically booked in late fall (October–November) for winter travel (January–March), creating a predictable fall passport rush.",
        tip: "Costa Rica requires at least one blank passport page and validity through your departure date. Check passport status in September before the fall booking season for winter Latin American travel.",
      },
    ],
    callout: {
      icon: "ri-store-2-line",
      title: "McMinnville, Oregon — Where Pinot Noir Meets the Pacific Northwest",
      description: "McMinnville sits at the intersection of world-class wine production, higher education, and Willamette Valley agriculture. The city's unique identity — historic downtown charm, Linfield University, and the heart of Oregon Pinot Noir country — creates distinctive international travel patterns. Fast Passport Center has served Yamhill County residents with expedited passports for wine trade shows, European wine tours, study abroad programs, and eco-adventure travel.",
    },
  },
  destinations: {
    title: "Where McMinnville Travelers Go — And Why Passports Get Urgent",
    intro: "These are the destinations and travel patterns we see most from McMinnville and Yamhill County clients. Wine industry trade, Linfield study abroad, European wine tourism, and Latin American eco-adventures create unique passport urgency situations.",
    stats: [
      { stat: "280 mi", label: "to Seattle Passport Agency — the closest regional passport office from McMinnville" },
      { stat: "45 mi", label: "to Portland International Airport (PDX) — primary international hub via OR-99W and I-5" },
      { stat: "1 hr", label: "to PDX — Yamhill County's most direct international departure point" },
      { stat: "2–9", label: "business days for Fast Passport Center expedited processing from McMinnville" },
    ],
    destinations: [
      { name: "France — Burgundy, Bordeaux, Champagne", context: "The #1 international destination for McMinnville wine professionals and enthusiasts", urgency: "Wine trade expos (Vinexpo, ProWein) 4–8 weeks out; wine tourism 3–6 months planned — passport checks happen at booking", icon: "ri-store-2-line" },
      { name: "Italy — Tuscany, Piedmont, Veneto", context: "Second only to France for Yamhill County wine travelers — villa rentals, wine tours, harvest exchanges", urgency: "Harvest exchange programs (August–October) confirmed 4–6 weeks before — tight expedited window", icon: "ri-store-2-line" },
      { name: "Spain — Rioja, Priorat, Barcelona", context: "Yamhill County wine industry expanding Spanish connections — trade shows and wine tourism growing", urgency: "Trade show invitations 6–8 weeks out; tourism typically planned 3–4 months ahead", icon: "ri-map-pin-2-line" },
      { name: "Costa Rica — Eco-Adventures & Coffee Tours", context: "Sustainable agriculture community draws McMinnville travelers to Costa Rica's coffee farms and eco-lodges", urgency: "November booking for January–March travel; passport expiration discovered 3–5 weeks before departure", icon: "ri-leaf-line" },
      { name: "Japan — Wine & Culinary Exchange", context: "Oregon Pinot Noir's growing reputation in Japan creates wine trade and cultural exchange travel", urgency: "Trade delegations planned 2–3 months ahead; expedited processing handles timelines comfortably", icon: "ri-ship-line" },
      { name: "Germany — Rheingau & Mosel Wine Regions", context: "German wine science and Riesling culture draws Yamhill County professionals to the Rheingau region", urgency: "ProWein Düsseldorf annual dates fixed; 4–8 weeks notice for attendees", icon: "ri-store-2-line" },
    ],
    crossLinksTitle: "Explore Nearby Oregon Service Areas",
    crossLinksDesc: "We serve travelers throughout the Willamette Valley and northwest Oregon with the same expedited passport expertise.",
    crossLinks: [
      { name: "Newberg", route: "/cities/newberg" },
      { name: "Salem", route: "/cities/salem" },
      { name: "Portland", route: "/cities/portland" },
    ],
  },
  vitalRecordsIcon: "ri-heart-pulse-line",
  vitalRecordsLinks: [
    { icon: "ri-user-smile-line", label: "Birth Certificate", desc: "Request certified birth certificates from the Oregon Health Authority Vital Records. Online, mail, or in-person options for Yamhill County residents.", url: "https://www.oregon.gov/oha/ph/certificates/" },
    { icon: "ri-file-list-2-line", label: "Death Certificate", desc: "Obtain certified death certificates through Oregon Health Authority Vital Records. Required for estate, insurance, and legal matters.", url: "https://www.oregon.gov/oha/ph/certificates/" },
    { icon: "ri-hearts-line", label: "Marriage Certificate", desc: "Request marriage license records from the Yamhill County Clerk. Certified copies available for name changes.", url: "https://www.co.yamhill.or.us/" },
    { icon: "ri-file-copy-line", label: "Divorce Decree", desc: "Obtain certified divorce records through the Yamhill County Circuit Court. Required for name change passport corrections.", url: "https://www.courts.oregon.gov/" },
  ],
};